import { colors, icons, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, KeyboardEvent, useRef, useState } from 'react'
import type { GuideSystem } from '../model/extend-guide'

const Dialog = styled(ui.ModalContainer)`
	width: min(1180px, 94vw);
	color: ${colors.blackish};
`

const Head = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 18px;
	padding: 28px 32px 8px;
`

const HeadIcon = styled.div`
	flex: none;
	display: grid;
	place-items: center;
	width: 52px;
	height: 52px;
	border-radius: 14px;
	background: ${colors.indigoBlue05};
`

const Title = styled.h2`
	margin: 2px 0 6px;
	font-size: 24px;
	line-height: 30px;
`

const Lead = styled.p`
	margin: 0;
	max-width: 720px;
	font-size: 15px;
	line-height: 22px;
	color: ${colors.lightGray};
`

const Close = styled.button`
	flex: none;
	display: grid;
	place-items: center;
	width: 36px;
	height: 36px;
	margin-left: auto;
	border: 0;
	border-radius: 50%;
	background: ${colors.indigoBlue05};
	color: ${colors.indigoBlue};
	cursor: pointer;

	svg {
		width: 14px;
		height: 14px;
	}
`

const Status = styled.div<{ ready: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	margin: 16px 32px 0;
	padding: 12px 16px;
	border-radius: 12px;
	font-size: 15px;
	background: ${({ ready }) =>
		ready ? colors.greenishCyan01 : colors.indigoBlue05};
	color: ${({ ready }) => (ready ? colors.greenishTeal : colors.indigoBlue)};
`

const Dot = styled.span<{ ready: boolean }>`
	flex: none;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: currentColor;
	animation: ${({ ready }) =>
		ready ? 'none' : 'extend-guide-pulse 1.4s ease-in-out infinite'};

	@keyframes extend-guide-pulse {
		50% {
			opacity: 0.25;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

const TabList = styled.div`
	display: flex;
	gap: 6px;
	margin: 20px 32px 0;
	border-bottom: 1px solid ${colors.brightGray};
`

const Tab = styled.button`
	margin-bottom: -1px;
	padding: 10px 14px;
	border: 0;
	border-bottom: 3px solid transparent;
	background: none;
	font: inherit;
	font-size: 15px;
	font-weight: 500;
	color: ${colors.lightGray};
	cursor: pointer;

	&[aria-selected='true'] {
		color: ${colors.indigoBlue};
		border-bottom-color: ${colors.indigoBlue};
	}
`

const Panel = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	padding: 8px 32px 4px;
	overflow: auto;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`

const Section = styled.section`
	min-width: 0;
	padding: 18px 0;

	& + & {
		margin-left: 35px;
		padding-left: 36px;
		border-left: 1px solid ${colors.brightGray};
	}

	@media (max-width: 900px) {
		& + & {
			margin-left: 0;
			padding-left: 0;
			border-left: 0;
			border-top: 1px solid ${colors.brightGray};
		}
	}
`

const SectionHead = styled.div`
	display: flex;
	align-items: baseline;
	gap: 12px;
	margin-bottom: 4px;

	h3 {
		margin: 0;
		font-size: 18px;
	}
`

const Badge = styled.span`
	padding: 2px 8px;
	border-radius: 10px;
	font-size: 12px;
	font-weight: 500;
	color: ${colors.indigoBlue};
	background: ${colors.indigoBlue05};
`

const Text = styled.p`
	margin: 0 0 14px;
	font-size: 14px;
	line-height: 21px;
	color: ${colors.lightGray};
`

const Keys = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin: 6px 0 14px;
	color: ${colors.lightGray};
`

const Key = styled.kbd`
	min-width: 40px;
	padding: 7px 12px;
	border: 1px solid ${colors.darkGray};
	border-radius: 8px;
	box-shadow: 0 2px 0 ${colors.darkGray};
	background: ${colors.white};
	font-family: inherit;
	font-size: 15px;
	font-weight: 500;
	text-align: center;
	color: ${colors.blackish};
`

const Picture = styled.button`
	display: block;
	width: 100%;
	padding: 0;
	overflow: hidden;
	border: 1px solid ${colors.brightGray};
	border-radius: 14px;
	background: ${colors.white};
	cursor: zoom-in;

	img {
		display: block;
		width: 100%;
		height: auto;
	}
`

const Steps = styled.ol`
	display: grid;
	gap: 8px;
	margin: 14px 0 0;
	padding: 0;
	list-style: none;

	li {
		display: flex;
		align-items: baseline;
		gap: 12px;
		font-size: 15px;
		line-height: 22px;
	}
`

const StepNumber = styled.span`
	flex: none;
	display: grid;
	place-items: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: ${colors.indigoBlue};
	color: ${colors.white};
	font-size: 13px;
	font-weight: 700;
`

const Note = styled.p`
	margin: 12px 0 0;
	font-size: 13px;
	line-height: 19px;
	color: ${colors.lightGray};
`

const Foot = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	padding: 18px 32px 24px;
	border-top: 1px solid ${colors.brightGray};
`

const HereLink = styled.button`
	margin-right: auto;
	padding: 0;
	border: 0;
	background: none;
	font: inherit;
	font-size: 14px;
	color: ${colors.indigoBlue};
	text-decoration: underline;
	cursor: pointer;
`

const Zoomed = styled.button`
	position: fixed;
	inset: 0;
	z-index: 1;
	display: grid;
	place-items: center;
	padding: 24px;
	border: 0;
	background: ${colors.black15};
	backdrop-filter: blur(2px);
	cursor: zoom-out;

	img {
		max-width: 100%;
		max-height: 100%;
		border-radius: 12px;
	}
`

const Bold = (chunks: string): JSX.Element => <b>{chunks}</b>

const MESSAGES = 'in_person.presenter.extend_guide'

type Way = {
	keys: string[]
	keysLead: string
	keysNotes: string[]
	settingsTitle: string
	steps: string[]
	settingsNotes: string[]
}

/** What each system's way says; the pictures come in as props. */
const WAYS: Record<GuideSystem, Way> = {
	mac: {
		keys: ['mac.key_command', 'mac.key_f1'],
		keysLead: 'mac.keys_lead',
		keysNotes: ['mac.keys_note_fn', 'mac.keys_note_moved'],
		settingsTitle: 'mac.settings_title',
		steps: ['mac.step_1', 'mac.step_2', 'mac.step_3', 'mac.step_4'],
		settingsNotes: ['mac.settings_note_main'],
	},
	windows: {
		keys: ['windows.key_windows', 'windows.key_p', 'windows.key_extend'],
		keysLead: 'windows.keys_lead',
		keysNotes: [],
		settingsTitle: 'windows.settings_title',
		steps: [
			'windows.step_1',
			'windows.step_2',
			'windows.step_3',
			'windows.step_4',
			'windows.step_5',
		],
		settingsNotes: ['windows.settings_note_moved'],
	},
}

const SYSTEMS: GuideSystem[] = ['mac', 'windows']

/** The guide's two pictures for one system. */
export type GuidePictures = { keys: string; settings: string }

type Props = {
	system: GuideSystem
	onSystem: (system: GuideSystem) => void
	pictures: Record<GuideSystem, GuidePictures>
	/** The TV is a second screen now: Present can open the presentation. */
	extended: boolean
	onPresent: () => void
	onPresentHere: () => void
	onClose: () => void
}

/**
 * What Present opens when the screens are mirrored - the TV shows this very
 * window, so it cannot show the presentation while the trainer keeps the
 * console. It shows the two ways to make the TV an extended screen, keys and
 * settings, for the trainer's system, and says the moment it is done; then
 * Present goes on as usual. "Present on this screen only" is the one-screen
 * way after all.
 */
export const ExtendScreensGuide: FC<Props> = ({
	system,
	onSystem,
	pictures,
	extended,
	onPresent,
	onPresentHere,
	onClose,
}) => {
	const translate = intl.useTranslate()
	const [zoomed, setZoomed] = useState<{ src: string; alt: string } | null>(
		null
	)
	const tabs = useRef<Record<GuideSystem, HTMLButtonElement | null>>({
		mac: null,
		windows: null,
	})
	const way = WAYS[system]
	const t = (key: string): string => translate(`${MESSAGES}.${key}`)
	const keysAlt = t(`${system}.keys_alt`)
	const settingsAlt = t(`${system}.settings_alt`)

	// Arrow keys move between the tabs, as in any tab list.
	const onTabKey = (event: KeyboardEvent<HTMLDivElement>): void => {
		if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
			return
		}
		const next = SYSTEMS[(SYSTEMS.indexOf(system) + 1) % SYSTEMS.length]
		onSystem(next)
		tabs.current[next]?.focus()
	}

	return (
		<ui.Modal
			onClose={onClose}
			onEsc={zoomed ? () => setZoomed(null) : onClose}
		>
			<Dialog
				role="dialog"
				aria-modal="true"
				aria-labelledby="extend-guide-title"
				data-testid="presenter.extend_guide"
			>
				<Head>
					<HeadIcon aria-hidden="true">
						<svg
							width="30"
							height="30"
							viewBox="0 0 30 30"
							fill="none"
							stroke={colors.indigoBlue}
							strokeWidth="2"
							strokeLinejoin="round"
						>
							<rect x="2" y="6" width="14" height="10" rx="1.5" />
							<path d="M6 20h6" />
							<rect
								x="14"
								y="10"
								width="14"
								height="10"
								rx="1.5"
								fill={colors.indigoBlue05}
							/>
							<path d="M18 24h6" />
						</svg>
					</HeadIcon>
					<div>
						<Title id="extend-guide-title">{t('title')}</Title>
						<Lead>{t('lead')}</Lead>
					</div>
					<Close
						type="button"
						aria-label={t('close')}
						onClick={onClose}
						data-testid="presenter.extend_guide.close"
					>
						<icons.CloseIcon />
					</Close>
				</Head>

				<Status
					ready={extended}
					role="status"
					data-testid="presenter.extend_guide.status"
					data-ready={extended}
				>
					<Dot ready={extended} aria-hidden="true" />
					{t(extended ? 'ready' : 'waiting')}
				</Status>

				<TabList role="tablist" onKeyDown={onTabKey}>
					{SYSTEMS.map(each => (
						<Tab
							key={each}
							ref={element => (tabs.current[each] = element)}
							type="button"
							role="tab"
							id={`extend-guide-tab-${each}`}
							aria-selected={each === system}
							aria-controls="extend-guide-panel"
							tabIndex={each === system ? 0 : -1}
							onClick={() => onSystem(each)}
							data-testid={`presenter.extend_guide.tab_${each}`}
						>
							{t(`tab_${each}`)}
						</Tab>
					))}
				</TabList>

				<Panel
					role="tabpanel"
					id="extend-guide-panel"
					aria-labelledby={`extend-guide-tab-${system}`}
				>
					<Section>
						<SectionHead>
							<h3>{t('keys_title')}</h3>
							<Badge>{t('fastest')}</Badge>
						</SectionHead>
						<Text>
							<intl.Translate
								b={Bold}
							>{`${MESSAGES}.${way.keysLead}`}</intl.Translate>
						</Text>
						<Keys>
							{way.keys.map((key, index) => (
								<span key={key}>
									{index > 0 ? (
										<span aria-hidden="true">{index === 2 ? '→ ' : '+ '}</span>
									) : null}
									<Key>{t(key)}</Key>
								</span>
							))}
						</Keys>
						<Picture
							type="button"
							aria-label={t('enlarge')}
							onClick={() =>
								setZoomed({ src: pictures[system].keys, alt: keysAlt })
							}
						>
							<img src={pictures[system].keys} alt={keysAlt} />
						</Picture>
						{way.keysNotes.map(note => (
							<Note key={note}>
								<intl.Translate
									b={Bold}
								>{`${MESSAGES}.${note}`}</intl.Translate>
							</Note>
						))}
					</Section>
					<Section>
						<SectionHead>
							<h3>{t(way.settingsTitle)}</h3>
						</SectionHead>
						<Picture
							type="button"
							aria-label={t('enlarge')}
							onClick={() =>
								setZoomed({
									src: pictures[system].settings,
									alt: settingsAlt,
								})
							}
						>
							<img src={pictures[system].settings} alt={settingsAlt} />
						</Picture>
						<Steps>
							{way.steps.map((step, index) => (
								<li key={step}>
									<StepNumber aria-hidden="true">{index + 1}</StepNumber>
									<span>
										<intl.Translate
											b={Bold}
										>{`${MESSAGES}.${step}`}</intl.Translate>
									</span>
								</li>
							))}
						</Steps>
						{way.settingsNotes.map(note => (
							<Note key={note}>
								<intl.Translate
									b={Bold}
								>{`${MESSAGES}.${note}`}</intl.Translate>
							</Note>
						))}
					</Section>
				</Panel>

				<Foot>
					<HereLink
						type="button"
						onClick={onPresentHere}
						data-testid="presenter.extend_guide.present_here"
					>
						{t('present_here')}
					</HereLink>
					<ui.ButtonPrimary
						small
						disabled={!extended}
						onClick={onPresent}
						data-testid="presenter.extend_guide.present"
					>
						<intl.Translate>in_person.presenter.present</intl.Translate>
					</ui.ButtonPrimary>
				</Foot>

				{zoomed ? (
					<Zoomed
						type="button"
						aria-label={t('close_picture')}
						onClick={() => setZoomed(null)}
						data-testid="presenter.extend_guide.zoomed"
					>
						<img src={zoomed.src} alt={zoomed.alt} />
					</Zoomed>
				) : null}
			</Dialog>
		</ui.Modal>
	)
}
